import { Injectable, inject } from '@angular/core';
import { LibFileManagementService } from '@tuain/ng-forms';
import { HttpClient } from '@angular/common/http';
import { saveAs } from 'file-saver';
import { take } from 'rxjs';
import { AppLoggerService } from '@services/app-logger.service';

@Injectable({
  providedIn: 'root',
})
export class FileManagementService extends LibFileManagementService {
  private http = inject(HttpClient);

  private _logger = inject(AppLoggerService);

  constructor() {
    super();
  }

  override openFile(fileBase64Data, fileName, _expFileType) {
    let fileType = _expFileType;
    if (this.hasHeaderComma(fileBase64Data)) {
      const [header, payload] = fileBase64Data.split(',');
      fileBase64Data = payload;
      fileType = header.split(';')[0].replace(/^data:/, '') || _expFileType;
    }
    const fileData = this.base64toBlob(fileBase64Data, fileType);
    // Se construye un blob con los datos binarios del archivo
    const blob = new Blob([fileData], { type: fileType });
    const url = window.URL.createObjectURL(blob);
    window.open(url);
    this.descargar(blob, fileName);
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  }

  override saveFileFromURL(fileUrl, fileName, _fileType) {
    this.http
      .get(fileUrl, { responseType: 'blob' })
      .pipe(take(1))
      .subscribe({
        next: blob => this.descargar(blob, fileName),
        error: err => this._logger.error('[FileManagement] Error descargando archivo:', err),
      });
  }

  override saveFile(fileBase64Data, fileName, fileType, hasHeader = true) {
    // El base64 nunca contiene comas: si hay coma, lo posterior es el payload y lo previo el header
    // (cubre data URL válido y el pseudo-data-URL "mime,base64" del gateway); si no, es base64 crudo.
    if (hasHeader && this.hasHeaderComma(fileBase64Data)) {
      const [header, payload] = fileBase64Data.split(',');
      fileBase64Data = payload;
      fileType = header.split(';')[0].replace(/^data:/, '') || fileType;
    }
    const fileData = this.base64toBlob(fileBase64Data, fileType);
    const blob = new Blob([fileData], { type: fileType });
    this.descargar(blob, fileName);
  }

  /**
   * Única puerta hacia `file-saver`.
   *
   * Existe para que las pruebas puedan interceptar la descarga. El builder de Karma pasó a
   * esbuild, que respeta la semántica de módulos ES: los export son de solo lectura y
   * `spyOn(fileSaver, 'saveAs')` falla con "is not declared writable or has no setter". Con
   * webpack funcionaba porque su interop de CommonJS los dejaba mutables.
   *
   * Lo que las pruebas verifican de verdad —el blob y el nombre de archivo que se arman— sigue
   * siendo lo mismo; esto solo mueve el punto de intercepción a código propio.
   */
  protected descargar(blob: Blob, fileName: string): void {
    saveAs(blob, fileName);
  }

  private hasHeaderComma(value): boolean {
    return typeof value === 'string' && value.includes(',');
  }

  base64toBlob(base64Data, contentType) {
    contentType = contentType || '';
    const sliceSize = 1024;
    const byteCharacters = atob(base64Data);
    const bytesLength = byteCharacters.length;
    const slicesCount = Math.ceil(bytesLength / sliceSize);
    const byteArrays = new Array(slicesCount);

    for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
      const begin = sliceIndex * sliceSize;
      const end = Math.min(begin + sliceSize, bytesLength);
      const bytes = new Array(end - begin);
      for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
        bytes[i] = byteCharacters[offset].charCodeAt(0);
      }
      byteArrays[sliceIndex] = new Uint8Array(bytes);
    }
    return new Blob(byteArrays, { type: contentType });
  }

  override printPdfFile(pdfDataBuffer) {
    const blob = new Blob([pdfDataBuffer], { type: 'application/pdf' });
    const blobUrl = window.URL.createObjectURL(blob);
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = blobUrl;
    document.body.appendChild(iframe);
    iframe.onload = () => {
      iframe.contentWindow.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
        window.URL.revokeObjectURL(blobUrl);
      }, 1000);
    };
  }
}
